import { getCustomComponent } from '@portal/processes/Content/contentBlockComponentsMap';
import { StickyHeaderWrapper } from '@portal/shared/components/StickyHeaderWrapper';
import { CanonicalLink } from '@portal/shared/layouts/CanonicalLink';
import { imageUrlBuilderFor } from '@portal/shared/utils/urlFor';
import { useHeaderContext } from '@portal/state/context/HeaderContext';
import { dynamicLanderViewEvent } from 'analytics/events/dynamicLanders';
import { type InferGetStaticPropsType } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import PreviewErrorBoundary from 'processes/Content/PreviewErrorBoundary';
import { PageProvider } from 'processes/Page/PageContext';
import React, { type ComponentProps, useEffect, useMemo } from 'react';
import { TranslationProvider } from 'shared/providers/TranslationProvider/TranslationProvider';
import { getActiveBannerForHeader } from 'shared/utils/bannerVisibility';
import { applyBannerReplacements } from 'shared/utils/contentReplacements';
import { useCloudfrontLocationQuery } from 'state/hooks/useCloudfrontLocation';
import { assetLibraryComponentsMap } from '@portal/shared/components/AssetLibrary/assetLibraryComponentsMap';
import { createVisibleOnPageLoadCalculator } from '../Content/createVisibleOnPageLoadCalculator';
import { renderSection } from '../Content/renderSection';
import { isSectionType } from '../Content/utils';
import { DynamicLanderLayout } from './DynamicLanderLayout';
import type { getStaticProps } from './getStaticProps';
import { addParamToUrl, isAssetLibrarySection, isSanityJobListSection } from './utils';

type LanderProps = InferGetStaticPropsType<typeof getStaticProps>;

const replaceBuildABundleLinks = (data: unknown, replacement?: string): unknown => {
  if (!replacement) return data;

  if (typeof data === 'string') {
    // Check if it's a URL string containing either path
    if (data.includes('/build-a-bundle') || data.includes('/get-your-ag1')) {
      // If it's an absolute URL (starts with http or https)
      if (data.match(/^https?:\/\//)) {
        const url = new URL(data);
        const path = url.pathname;
        // Only replace if the path contains our target strings
        if (path.includes('/build-a-bundle') || path.includes('/get-your-ag1')) {
          return url.origin + replacement;
        }
        return data;
      }
      // If it's a relative URL, just use the replacement as is
      return replacement;
    }
    return data;
  }

  if (Array.isArray(data)) {
    return data.map((item) => replaceBuildABundleLinks(item, replacement));
  }

  if (typeof data === 'object' && data !== null) {
    const newObj: Record<string, unknown> = {};
    Object.entries(data as object).forEach(([key, value]) => {
      // Special handling for btnLink.external
      if (key === 'btnLink' && typeof value === 'object' && value !== null) {
        const btnLink = value as { external?: string };
        if (
          btnLink.external &&
          (btnLink.external.includes('/build-a-bundle') ||
            btnLink.external.includes('/get-your-ag1'))
        ) {
          newObj[key] = {
            ...btnLink,
            external: replaceBuildABundleLinks(btnLink.external, replacement)
          };
          return;
        }
      }
      newObj[key] = replaceBuildABundleLinks(value, replacement);
    });
    return newObj;
  }

  return data;
};

export const DynamicLander = ({
  pageContextProps,
  pageTitle,
  seoData,
  items,
  pageId,
  locale,
  preview,
  headerData: initialHeaderData,
  footerData,
  bannerData,
  productsInventory,
  usedProductsMap
}: LanderProps) => {
  const router = useRouter();
  const { data: cloudfrontLocation } = useCloudfrontLocationQuery();

  const { getHeaderDataDefaults, useSetHeaderOptions } = useHeaderContext();
  useSetHeaderOptions(initialHeaderData);
  const headerData = getHeaderDataDefaults(initialHeaderData);

  const activeBanner = useMemo(
    () =>
      getActiveBannerForHeader({
        bannerData: bannerData || [],
        country: cloudfrontLocation?.country,
        path: router.asPath
      }),
    [bannerData, cloudfrontLocation, router.asPath]
  );

  // Transform items with banner replacements only if we have an activeBanner
  const transformedItems = useMemo(() => {
    const splittedPathName = router.asPath.split('/');
    const landerUrlParam = `lander=${splittedPathName[splittedPathName.length - 1]}`;

    const doTransform = (item: (typeof items)[keyof typeof items][number]): typeof item => {
      // Create deep copy to avoid mutations
      const itemCopy = JSON.parse(JSON.stringify(item));

      // Only apply banner replacements if we have a valid banner
      if (activeBanner) {
        if (itemCopy.section) {
          itemCopy.section = applyBannerReplacements(itemCopy.section, activeBanner);
        }
        if (itemCopy.customSection) {
          itemCopy.customSection = {
            ...itemCopy.customSection,
            customContent: applyBannerReplacements(
              itemCopy.customSection.customContent,
              activeBanner
            )
          };
        }
      }

      // Then handle specific build-a-bundle URL replacements
      if (activeBanner?.globalBabReplace) {
        if (itemCopy.section) {
          // Handle specific button/link fields
          if (itemCopy.section.marketingButton?.marketingLink) {
            itemCopy.section.marketingButton = {
              ...itemCopy.section.marketingButton,
              marketingLink: replaceBuildABundleLinks(
                itemCopy.section.marketingButton.marketingLink,
                activeBanner.globalBabReplace
              ) as string
            };
          }

          if (itemCopy.section.unifiedButton?.bundleUrl) {
            itemCopy.section.unifiedButton = {
              ...itemCopy.section.unifiedButton,
              bundleUrl: replaceBuildABundleLinks(
                itemCopy.section.unifiedButton.bundleUrl,
                activeBanner.globalBabReplace
              ) as string
            };
          }

          if (itemCopy.section.unifiedButton?.baseButtonLinkV2?.btnLink?.internal?.slug?.current) {
            itemCopy.section.unifiedButton = {
              ...itemCopy.section.unifiedButton,
              baseButtonLinkV2: {
                ...itemCopy.section.unifiedButton.baseButtonLinkV2,
                btnLink: {
                  ...itemCopy.section.unifiedButton.baseButtonLinkV2.btnLink,
                  internal: {
                    ...itemCopy.section.unifiedButton.baseButtonLinkV2.btnLink.internal,
                    slug: {
                      ...itemCopy.section.unifiedButton.baseButtonLinkV2.btnLink.internal.slug,
                      current: replaceBuildABundleLinks(
                        itemCopy.section.unifiedButton.baseButtonLinkV2.btnLink.internal.slug
                          .current,
                        activeBanner.globalBabReplace
                      ) as string
                    }
                  }
                }
              }
            };
          }

          if (itemCopy.section.unifiedButton?.baseButtonLinkV2?.btnLink?.external) {
            itemCopy.section.unifiedButton = {
              ...itemCopy.section.unifiedButton,
              baseButtonLinkV2: {
                ...itemCopy.section.unifiedButton.baseButtonLinkV2,
                btnLink: {
                  ...itemCopy.section.unifiedButton.baseButtonLinkV2.btnLink,

                  external: replaceBuildABundleLinks(
                    itemCopy.section.unifiedButton.baseButtonLinkV2.btnLink.external,
                    activeBanner.globalBabReplace
                  ) as string
                }
              }
            };
          }
        }
      }

      // add lander param to the bab link
      if (itemCopy.section) {
        if (
          itemCopy.section.unifiedButton?.bundleUrl &&
          !itemCopy.section.unifiedButton?.bundleUrl.includes('lander=')
        ) {
          const newBundleUrl = addParamToUrl(
            itemCopy.section.unifiedButton?.bundleUrl,
            landerUrlParam
          );
          itemCopy.section.unifiedButton = {
            ...itemCopy.section.unifiedButton,
            bundleUrl: newBundleUrl
          };
        }

        if (
          itemCopy.section.unifiedButton?.baseButtonLinkV2?.btnLink?.external &&
          !itemCopy.section.unifiedButton?.baseButtonLinkV2?.btnLink?.external.includes('lander=')
        ) {
          const newBundleUrl = addParamToUrl(
            itemCopy.section.unifiedButton?.baseButtonLinkV2?.btnLink?.external,
            landerUrlParam
          );
          itemCopy.section.unifiedButton = {
            ...itemCopy.section.unifiedButton,
            baseButtonLinkV2: {
              ...itemCopy.section.unifiedButton.baseButtonLinkV2,
              btnLink: {
                ...itemCopy.section.unifiedButton.baseButtonLinkV2.btnLink,
                external: newBundleUrl
              }
            }
          };
        }
      }

      return itemCopy;
    };

    // Determine the bottom-most header with a banner. If there is one, strip
    // all header items above it to prevent multiple banners or headers from
    // showing.
    let headerItems = items.headerItems.map(doTransform);
    if (activeBanner) {
      const lastHeaderIndexWithBanner = headerItems.findLastIndex(
        (i: any) => i.section && isSectionType(i.section, ['headerFullV2', 'headerLanderV3'])
      );
      if (lastHeaderIndexWithBanner > 0) {
        headerItems = headerItems.slice(lastHeaderIndexWithBanner);
      }
    }

    return {
      headerItems,
      bodyItems: items.bodyItems.map(doTransform)
    };
  }, [items, activeBanner, router.asPath]);

  useEffect(() => {
    dynamicLanderViewEvent({ locale, path: window.location.pathname });
  }, [locale, pageId]);

  const components = useMemo(() => {
    const doTransform = (
      { section, customSection }: (typeof transformedItems)[keyof typeof transformedItems][number],
      { visibleOnPageLoad }: { visibleOnPageLoad: boolean } = { visibleOnPageLoad: false }
    ) => {
      if (customSection) {
        const Component = getCustomComponent(customSection.section.name);

        if (!Component || !customSection.customContent) {
          return null;
        }

        return (
          <PreviewErrorBoundary
            preview={preview}
            section={customSection.section.name}
            key={customSection.section.id}>
            <Component
              slug={customSection.section.id}
              customContent={customSection.customContent}
              visibleOnPageLoad={visibleOnPageLoad}
            />
          </PreviewErrorBoundary>
        );
      }

      if (!section) {
        return null;
      }

      if (isSanityJobListSection(section)) {
        return null;
      }

      if (isAssetLibrarySection(section)) {
        const Component = assetLibraryComponentsMap[section.name];

        if (!section.props) {
          return null;
        }

        // TODO: `ComponentProps<any>` is a temp solution
        return <Component key={section.id} {...(section.props as ComponentProps<any>)} />;
      }

      return renderSection(section, {
        preview,
        headerData,
        productsInventory,
        usedProductsMap,
        visibleOnPageLoad,
        activeBanner
      });
    };

    const headerCount = transformedItems.headerItems.length;
    const headerItems = transformedItems.headerItems.map((i, index) => {
      const component = doTransform(i, { visibleOnPageLoad: true });
      if (!component) {
        return null;
      }
      return (
        // eslint-disable-next-line react/no-array-index-key
        <div key={index} style={{ position: `relative`, zIndex: headerCount - index }}>
          {component}
        </div>
      );
    });

    const visibleOnPageLoadCalculator = createVisibleOnPageLoadCalculator();

    const bodyItems = transformedItems.bodyItems.map((item) => {
      const visibleOnPageLoad = visibleOnPageLoadCalculator({
        section: item.section,
        customSectionName: item.customSection?.section.name
      });

      return doTransform(item, {
        visibleOnPageLoad
      });
    });

    const result: React.ReactNode[] = [];
    if (headerItems.length) {
      result.push(
        <StickyHeaderWrapper key="sticky-header-wrapper">{headerItems}</StickyHeaderWrapper>
      );
    }
    return [...result, ...bodyItems];
  }, [
    headerData,
    preview,
    productsInventory,
    usedProductsMap,
    transformedItems.bodyItems,
    transformedItems.headerItems,
    activeBanner
  ]);

  const structuredSchemaData = useMemo(
    () =>
      (seoData?.structured_schema_data || []).map(
        (schema: { jsonSchemaData: string; schemaType: string }) => (
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: schema.jsonSchemaData }}
            key={schema.schemaType}
          />
        )
      ),
    [seoData?.structured_schema_data]
  );

  const seoImageData = useMemo(() => {
    if (seoData?.open_graph_image_sanity_ref_id) {
      return imageUrlBuilderFor(seoData.open_graph_image_sanity_ref_id).url();
    }
    return undefined;
  }, [seoData?.open_graph_image_sanity_ref_id]);

  return (
    <TranslationProvider>
      <PageProvider {...pageContextProps}>
        <Head>
          <title>{seoData?.title || pageTitle || `AG1®`}</title>
          <meta property="og:type" content="website" />
          <meta property="og:title" content={seoData?.title || pageTitle || `AG1®`} />
          {seoData?.description && <meta name="description" content={seoData?.description} />}
          {seoImageData && <meta property="og:image" content={seoImageData} />}
          {seoData?.keywords && <meta name="keywords" content={seoData.keywords} />}
          {seoData?.is_no_index_page && <meta name="robots" content="noindex,nofollow" />}
          {structuredSchemaData}
        </Head>
        <CanonicalLink />
        <DynamicLanderLayout footerData={footerData}>{components}</DynamicLanderLayout>
      </PageProvider>
    </TranslationProvider>
  );
};
